import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CreditCard, CheckCircle, Clock, AlertTriangle, Sparkles, ArrowRight, Shield, FileText, X } from 'lucide-react';

const PLANS = [
  {
    id: 'starter_monthly', name: 'Starter', price: 1999, maxUsers: 50,
    description: 'For small schools',
    features: ['Up to 50 users', 'All leave types', 'Analytics dashboard', 'Priority email support']
  },
  {
    id: 'professional_monthly', name: 'Professional', price: 4999, maxUsers: 500,
    description: 'For growing schools', popular: true,
    features: ['Up to 500 users', 'Everything in Starter', 'Advanced reports & export', 'Custom leave policies', 'Bulk user import', 'Phone & email support']
  },
  {
    id: 'enterprise_monthly', name: 'Enterprise', price: 14999, maxUsers: 10000,
    description: 'For school chains & trusts',
    features: ['Up to 10,000 users', 'Everything in Professional', 'Multi-branch support', 'Dedicated account manager', 'SLA guarantee (99.9%)', '24/7 priority support']
  }
];

export const Billing = () => {
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, histRes] = await Promise.all([
        api.get('/billing/subscription'),
        api.get('/billing/history'),
      ]);
      setSubscription(subRes.data.data);
      setTransactions(histRes.data.data.transactions || []);
    } catch (error) {
      toast.error('Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment verification timed out. Please refresh.');
      return;
    }
    try {
      const response = await api.get(`/billing/checkout/status/${sessionId}`);
      if (response.data.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        window.history.replaceState({}, '', '/dashboard/billing');
        fetchData();
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      toast.error('Failed to verify payment');
    }
  };

  const handleUpgrade = async (planId) => {
    setProcessing(planId);
    try {
      const origin = window.location.origin;
      const isChangePlan = subscription?.status === 'active' && subscription?.plan_id !== 'trial';
      const endpoint = isChangePlan ? '/billing/change-plan' : '/billing/create-checkout';
      const response = await api.post(endpoint, { plan_id: planId, origin_url: origin });
      window.location.href = response.data.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="billing-loading">
        <div className="h-8 w-8 border-2 border-teal-500/30 border-t-teal-500 rounded-full animate-spin" />
      </div>
    );
  }

  const isTrial = subscription?.plan_id === 'trial';
  const isExpired = subscription?.trial_expired;
  const daysLeft = subscription?.trial_days_remaining || 0;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md shadow-teal-500/20">
          <CreditCard className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="billing-title">Billing & Subscription</h1>
          <p className="text-sm text-slate-500">Manage your plan and billing</p>
        </div>
      </div>

      {/* Current Plan Card */}
      <div className={`glass-card p-6 border-l-4 ${isExpired ? 'border-l-rose-500' : isTrial ? 'border-l-amber-500' : 'border-l-emerald-500'}`} data-testid="current-plan">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${isExpired ? 'bg-rose-100' : isTrial ? 'bg-amber-100' : 'bg-emerald-100'}`}>
              {isExpired ? <AlertTriangle className="h-6 w-6 text-rose-600" /> : isTrial ? <Clock className="h-6 w-6 text-amber-600" /> : <Shield className="h-6 w-6 text-emerald-600" />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-800" data-testid="plan-name">{subscription?.plan_name}</h2>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${isExpired ? 'bg-rose-100 text-rose-700' : isTrial ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`} data-testid="plan-status">
                  {isExpired ? 'Expired' : subscription?.status === 'active' ? 'Active' : 'Trial'}
                </span>
              </div>
              {isTrial && !isExpired && (
                <p className="text-sm text-amber-600 font-medium mt-1" data-testid="trial-days">
                  {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining in your free trial
                </p>
              )}
              {isExpired && (
                <p className="text-sm text-rose-600 font-medium mt-1" data-testid="trial-expired-msg">
                  Your free trial has ended. Subscribe to continue using all features.
                </p>
              )}
              {!isTrial && subscription?.amount && (
                <p className="text-sm text-slate-500 mt-1" data-testid="plan-price">
                  Rs.{subscription.amount?.toLocaleString('en-IN')}/month
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-800" data-testid="users-count">{subscription?.users_count}</p>
              <p className="text-xs text-slate-400">Users</p>
            </div>
            <div className="h-10 w-px bg-slate-200" />
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-800" data-testid="max-users">{subscription?.max_users}</p>
              <p className="text-xs text-slate-400">Max Users</p>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Banner for Trial */}
      {isTrial && (
        <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-6 text-white" data-testid="upgrade-banner">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="h-6 w-6" />
              <div>
                <h3 className="font-bold text-lg">Upgrade to unlock full potential</h3>
                <p className="text-teal-100 text-sm">Get unlimited leave types, advanced reports, and premium support</p>
              </div>
            </div>
            <a href="#plans" className="flex items-center gap-2 bg-white text-teal-700 font-semibold px-5 py-2.5 rounded-xl hover:bg-teal-50 transition-colors text-sm">
              View Plans <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}

      {/* Plans Grid */}
      <div id="plans">
        <h2 className="text-lg font-bold text-slate-800 mb-4">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="plans-grid">
          {PLANS.map((plan) => {
            const isCurrent = subscription?.plan_id === plan.id;
            const currentPrice = PLANS.find(p => p.id === subscription?.plan_id)?.price || 0;
            const isUpgrade = plan.price > currentPrice;
            const isDowngrade = plan.price < currentPrice && !isTrial;
            const buttonLabel = isTrial ? 'Subscribe' : isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Switch';
            return (
              <div
                key={plan.id}
                className={`relative glass-card p-6 flex flex-col transition-all duration-200 hover:shadow-lg ${plan.popular ? 'ring-2 ring-teal-500' : ''} ${isCurrent ? 'border-emerald-300 bg-emerald-50/30' : ''}`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full">Most Popular</span>
                  </div>
                )}
                <div className="mb-4 mt-1">
                  <h3 className="text-lg font-bold text-slate-800">{plan.name}</h3>
                  <p className="text-xs text-slate-500">{plan.description}</p>
                </div>
                <div className="mb-5">
                  <span className="text-3xl font-extrabold text-slate-900">Rs.{plan.price.toLocaleString('en-IN')}</span>
                  <span className="text-sm text-slate-400 ml-1">/month</span>
                  <p className="text-xs text-slate-400 mt-1">Up to {plan.maxUsers.toLocaleString()} users</p>
                </div>
                <ul className="space-y-2 flex-1 mb-5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <div className="text-center text-sm text-emerald-600 font-semibold py-2.5 bg-emerald-50 rounded-xl" data-testid={`current-${plan.id}`}>
                    Current Plan
                  </div>
                ) : (
                  <Button
                    className={`w-full rounded-xl font-semibold ${isDowngrade ? 'bg-amber-100 hover:bg-amber-200 text-amber-900' : plan.popular ? 'bg-teal-600 hover:bg-teal-500 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-900'}`}
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={processing !== null}
                    data-testid={`upgrade-${plan.id}-btn`}
                  >
                    {processing === plan.id ? (
                      <div className="h-4 w-4 border-2 border-current/30 border-t-current rounded-full animate-spin mr-2" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2" />
                    )}
                    {buttonLabel}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment info */}
      <div className="text-center text-xs text-slate-400 space-y-1 pt-2">
        <p>All prices are in INR and exclude GST (18%). Secure payments via Stripe.</p>
        <p>Need help? <a href="/contact" className="text-teal-600 hover:underline">Contact our sales team</a></p>
      </div>

      {/* Billing History */}
      {transactions.length > 0 && (
        <div data-testid="billing-history">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Billing History</h2>
          <div className="glass-card overflow-hidden">
            <div className="grid grid-cols-4 bg-slate-50 border-b border-slate-100 px-4 py-3">
              <span className="text-xs font-semibold text-slate-400 uppercase">Date</span>
              <span className="text-xs font-semibold text-slate-400 uppercase">Plan</span>
              <span className="text-xs font-semibold text-slate-400 uppercase">Amount</span>
              <span className="text-xs font-semibold text-slate-400 uppercase">Status</span>
            </div>
            {transactions.slice(0, 10).map((t) => {
              const plan = PLANS.find(p => p.id === t.plan_id);
              const statusClasses = {
                paid: 'bg-emerald-100 text-emerald-700',
                pending: 'bg-amber-100 text-amber-700',
                failed: 'bg-rose-100 text-rose-700',
              };
              return (
                <div key={t.transaction_id} className="grid grid-cols-4 px-4 py-3 border-b border-slate-50 last:border-0 text-sm" data-testid={`tx-${t.transaction_id}`}>
                  <span className="text-slate-600">{new Date(t.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  <span className="font-medium text-slate-800">{plan?.name || t.plan_id}</span>
                  <span className="text-slate-800">Rs.{t.amount?.toLocaleString('en-IN')}</span>
                  <span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${statusClasses[t.payment_status] || 'bg-slate-100 text-slate-600'}`}>
                      {t.payment_status}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Cancel Subscription */}
      {!isTrial && subscription?.status === 'active' && (
        <div className="glass-card p-4 border border-rose-100" data-testid="cancel-section">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">Cancel Subscription</p>
              <p className="text-xs text-slate-400">You'll lose access to premium features at the end of your billing period.</p>
            </div>
            <Button
              variant="outline"
              className="border-rose-200 text-rose-600 hover:bg-rose-50 text-xs"
              onClick={async () => {
                if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
                setCancelling(true);
                try {
                  await api.post('/billing/cancel');
                  toast.success('Subscription cancelled');
                  fetchData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to cancel');
                } finally {
                  setCancelling(false);
                }
              }}
              disabled={cancelling}
              data-testid="cancel-subscription-btn"
            >
              {cancelling ? 'Cancelling...' : 'Cancel Plan'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
